/**
 * Shared risk-address (risicoadres) state for property products.
 *
 * The policyholder address lives in core's useSharedAddress (sharedData.address).
 * Property products (Woonhuis/Inboedel) may insure a building at a *different*
 * address; that risk address is collected once and reused across them. It is a
 * separate slice (sharedData.riskAddress) plus a shared Ja/Nee answer
 * (sharedData.riskAddressSame) — both products insure the same property, so the
 * answer is consistent — and the product that collected it (riskAddressSource),
 * so that product keeps its editable block instead of a read-only echo of its own input.
 *
 * SAA-side on purpose: useWorkflowExtras's saveSharedData shallow-merges the
 * returned top-level keys, so writing riskAddress/riskAddressSame never disturbs
 * the policyholder slice. No core changes required.
 */
import { useCallback, useMemo } from 'react';
import { useWorkflowExtras } from '@sluitstraat/core';
import type { BAAddress, SaaProductId } from '../utils/addressSharing';

export type RiskAddressSame = 'J' | 'N';

export const useSharedRiskAddress = () => {
  const { sharedData, saveSharedData } = useWorkflowExtras();

  const riskAddress: BAAddress = useMemo(
    () => sharedData?.riskAddress || {},
    [sharedData?.riskAddress],
  );

  const riskAddressSame: RiskAddressSame = sharedData?.riskAddressSame ?? 'J';
  const riskAddressSource: SaaProductId | null = sharedData?.riskAddressSource ?? null;

  const updateRiskAddress = useCallback(
    (next: Partial<BAAddress>, source?: SaaProductId) => {
      saveSharedData((prev: any) => ({
        riskAddress: { ...prev?.riskAddress, ...next },
        ...(source ? { riskAddressSource: source } : {}),
      }));
    },
    [saveSharedData],
  );

  const setRiskAddressSame = useCallback(
    (value: RiskAddressSame) => {
      saveSharedData({ riskAddressSame: value });
    },
    [saveSharedData],
  );

  const clearRiskAddress = useCallback(() => {
    saveSharedData({ riskAddress: {}, riskAddressSource: null });
  }, [saveSharedData]);

  return { riskAddress, riskAddressSame, riskAddressSource, updateRiskAddress, setRiskAddressSame, clearRiskAddress };
};
